import React, { Component, lazy } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isMobile } from 'react-device-detect';

import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import LocalStorageService from 'services/LocalStorageService';
import UserService from 'services/UserService';
import UserStore from 'stores/UserStore';
import { MOBILE_MENU } from 'pages/Drawer/Drawers';
import Disclaimer from 'components/Disclaimer/Disclaimer';
import TwoFaContextWithModal from 'context/TwoFaContext';
import {
  BODY_NO_PADDING,
  COUPLE,
  GET_UI_VARIABLES_TIME,
  QUICK_FILTERS_MENU,
  PARSED_CHECKING_FOR_CRAWLERS,
} from 'enum/helpers';
import routeDefinitions from 'enum/routeDefinitions';
import studioProtectedPages from 'enum/studioProtectedPages';
import allUserGroups from 'enum/userGroups';
import {
  actionCreators as applicationActions,
  selector as applicationSelector,
} from 'app/AppState';
import {
  actionCreators as directoryListActions,
  selector as directorySelector,
} from 'pages/Directory/State';
import TrackedRoute from 'overwrites/TrackedRoute';

import styles from './DefaultLayout.css';


const Header = lazy(() => import('components/Header/Header'));
const Footer = lazy(() => import('components/Footer/Footer'));
const PerformerHubFooter = lazy(() => import('components/PerformerHubFooter/PerformerHubFooter'));
const Intercom = lazy(() => import('components/Intercom/Intercom'));

const redirectMap = {
  '/cams/private': '/female/private-show',
  '/cams/solo': 'female/regular',
  'cams/new': 'female/new',
  '/cams/free': routeDefinitions.root,
  '/cams/spy': routeDefinitions.root,
  '/cams/hd': routeDefinitions.root,
  '/cams/mobile': routeDefinitions.root,
  '/cams/featured': routeDefinitions.root,
  '/female/cams/online/hd': routeDefinitions.root,
  '/recent': routeDefinitions.root,
  '/cams/vr': routeDefinitions.root,
  '/cams/smoking': routeDefinitions.root,
};

@connect(applicationSelector, (dispatch) => {
  return { appActions: bindActionCreators(applicationActions, dispatch) };
})
@connect(directorySelector, (dispatch) => {
  return { directoryActions: bindActionCreators(directoryListActions, dispatch) };
})
export default class DefaultLayout extends Component {
  static propTypes = {
    Page: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    appActions: PropTypes.object.isRequired,
    availableLanguages: PropTypes.array.isRequired,
    balance: PropTypes.number.isRequired,
    computedMatch: PropTypes.object.isRequired,
    directoryActions: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    drawer: PropTypes.any.isRequired,
    drawerActions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    userActions: PropTypes.object.isRequired,
    exact: PropTypes.bool,
    hasPartialProps: PropTypes.bool,
    showRegistrationModal: PropTypes.bool,
    twoFaContext: PropTypes.object,
  };

  static defaultProps = {
    hasPartialProps: false,
    exact: false,
    showRegistrationModal: false,
    twoFaContext: null,
  };

  constructor(...args) {
    super(...args);

    this.state = {
      showModal: false,
      genderPreference: '',
      showGuestBillingModal: false,
      headerLoaded: false,
    };

    this.getUiVariablesTimer = null;
  }

  componentDidMount() {
    if (ControlledFeatureService.setUser(this.props.user).has(allUserGroups.PROFILE_V2_FORCE_REFRESH)) {
      UserService.getUiVariables();

      this.getUiVariablesTimer = setInterval(() => {
        UserService.getUiVariables();
      }, GET_UI_VARIABLES_TIME);
    }
  }

  componentWillUnmount() {
    if (this.getUiVariablesTimer) {
      clearInterval(this.getUiVariablesTimer);
    }
  }

  get isPageWithIntercom() {
    return studioProtectedPages.includes(this.props.path);
  }

  changeGenderPreference = (saveAsDefault) => {
    const { userActions, user } = this.props;

    this.setState({ showModal: false }, () => {
      if (saveAsDefault) {
        userActions.updateGenderPreference(user.username, this.state.genderPreference);
      }
    });
  };

  closeModal = () => this.setState({ showModal: false });

  // Function created temporarily for redirect purposes due to last minute changes.
  // It will be removed once permanent redirects will be implemented server side
  routeMapping = (path) => {
    const { location, history } = this.props;
    const mapping = redirectMap[location.pathname];
    if (!mapping) {
      return path;
    }

    history.push(mapping);

    return undefined;
  };

  setGuestBilllingModalProp = (val) => {
    this.setState({ showGuestBillingModal: val });
  };

  get hasDisclaimerCookie() {
    return CookieStorageService.hasCookie('disclaimer18');
  }

  onLoaded = () => this.setState({ headerLoaded: true });

  onGenderChange = (gender) => {
    const { drawerActions } = this.props;
    const modalShown = LocalStorageService.get('genderModal');

    drawerActions.toggle(MOBILE_MENU);

    if (gender !== COUPLE) {
      if (!modalShown) {
        setTimeout(() => this.setState({ showModal: true, genderPreference: gender }), 0);

        LocalStorageService.set('genderModal', 'true');
      } else {
        setTimeout(() => this.setState({ genderPreference: gender }), 0);
      }
    }
  };

  renderPratialContent = (routeProps) => {
    const { Page, userActions } = this.props;

    return <Page {...routeProps} showBuyTokensModal={userActions} />;
  };

  renderContent = ({ match }) => {
    const { Page, exact, path, computedMatch, balance, twoFaContext, hasPartialProps, ...rest } = this.props;

    if (twoFaContext) {
      return (
        <TwoFaContextWithModal {...twoFaContext}>
          <Page {...rest} match={match} />
        </TwoFaContextWithModal>
      );
    }

    return <Page {...rest} match={match} />;
  };

  renderRoute(processedPath) {
    if (!this.state.headerLoaded) {
      return null;
    }

    const { exact, hasPartialProps } = this.props;
    const routeRender = hasPartialProps ? this.renderPratialContent : this.renderContent;

    return (
      <div className="pageContent">
        <TrackedRoute
          exact={exact}
          path={processedPath}
          render={routeRender}
        />
      </div>
    );
  }

  render() {
    const { showGuestBillingModal } = this.state;
    const { path, user, drawer } = this.props;

    const processedPath = this.routeMapping(path);

    const bodyElem = document.querySelector('body');
    bodyElem.classList.remove(BODY_NO_PADDING);

    const isStudio = Boolean(user.user.getIn(['user', 'studio', 'username']));
    const isUserLoggedIn = Boolean(user.username);
    const hasBgImageCF = UserStore.hasCF(allUserGroups.BACKGROUND_IMAGE_STATIC_RESOURCE);
    const { preLander, studioInvitation, studioEmail, onboardingNewUser, userLogin } = routeDefinitions;
    const performerHubLayout = [preLander, studioInvitation, studioEmail];
    const isPrelanderPage = [preLander, studioInvitation].includes(processedPath);
    const isOnboardingNewUserPage = onboardingNewUser.includes(processedPath);
    const isPerformerHub = performerHubLayout.includes(processedPath);
    const isLoginPage = processedPath === userLogin;
    const favortitesDrawerOpened = Boolean(drawer?.get(QUICK_FILTERS_MENU));

    const shouldShowDisclaimer =
      !PARSED_CHECKING_FOR_CRAWLERS &&
      path !== routeDefinitions.legal &&
      !isMobile &&
      !isUserLoggedIn &&
      !this.hasDisclaimerCookie &&
      hasBgImageCF;

    const { history, userActions, dispatch, availableLanguages } = this.props;

    if (shouldShowDisclaimer) {
      return (
        <div className={styles.disclaimerOverlay}>
          <Disclaimer
            history={history}
            user={user}
            userActions={userActions}
          />
        </div>
      );
    }

    const { computedMatch, match, appActions, drawerActions, balance, showRegistrationModal } = this.props;

    return (
      <div className="pageContainer">
        <Header
          appActions={appActions}
          availableLanguages={availableLanguages}
          balance={balance}
          computedMatch={computedMatch}
          dispatch={dispatch}
          drawer={drawer}
          drawerActions={drawerActions}
          history={history}
          isLoginPage={isLoginPage}
          isPerformerHub={isPerformerHub}
          isPrelanderPage={isPrelanderPage}
          match={match}
          processedPath={processedPath}
          setGuestBilllingModalProp={this.setGuestBilllingModalProp}
          showGuestBillingModal={showGuestBillingModal}
          showRegistrationModal={showRegistrationModal}
          user={user}
          userActions={userActions}
          onGenderChange={this.onGenderChange}
          onLoaded={this.onLoaded}
        />

        {this.renderRoute(processedPath)}

        {!isPerformerHub ? (
          <Footer
            availableLanguages={availableLanguages}
            dispatch={dispatch}
            history={history}
            isStudio={isStudio}
            path={processedPath}
            user={user}
            userActions={userActions}
          />
        ) : (
          <PerformerHubFooter
            availableLanguages={availableLanguages}
            dispatch={dispatch}
            isOnboardingNewUserPage={isOnboardingNewUserPage}
            isPrelanderPage={isPrelanderPage}
            user={user}
            userActions={userActions}
          />
        )}

        {this.isPageWithIntercom && !favortitesDrawerOpened && <Intercom user={user} />}
      </div>
    );
  }
}
